import { useEffect, useState, useCallback, startTransition } from 'react';

import { TextField, Divider, Box } from '@mui/material';
import { useReactFlow } from '@xyflow/react';
import { useTranslate } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';

import { flowchartLogger } from 'src/lib/logger';

import SortableInputsList from '../../components/SortableInputsList';
import SoundfileList from '../../components/SoundfileList';
import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps, SortableInput } from '../../dataProvider/types';
import type { Soundfile } from '@ragu-components/ord-types';

export interface IVRType extends Record<string, unknown> {
  selections?: SortableInput[];
  errorSoundfile?: Soundfile | null;
  menuSoundfile?: Soundfile | null;
  retries?: number;
  timeout?: number;
  timeoutSoundfile?: Soundfile | null;
}

interface IVRControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: IVRType;
}

export default function IVRControls({ updateNodeData, data }: IVRControlsProps): React.ReactElement {
  const [selections, setSelections] = useState<SortableInput[]>(
    data.selections && data.selections.length > 0 ? data.selections : [{ id: uuidv4(), value: '' }]
  );
  const [errorSoundfile, setErrorSoundfile] = useState<Soundfile | null>(data.errorSoundfile ?? null);
  const [menuSoundfile, setMenuSoundfile] = useState<Soundfile | null>(data.menuSoundfile ?? null);
  const [timeoutSoundfile, setTimeoutSoundfile] = useState<Soundfile | null>(data.timeoutSoundfile ?? null);
  const sanitizeTimeout = useCallback((value: number | null | undefined): number => {
    return typeof value === 'number' && !Number.isNaN(value) ? value : 1000;
  }, []);
  const sanitizeRetries = useCallback((value: number | null | undefined): number => {
    return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
  }, []);
  const [timeout, setTimeoutProp] = useState<number>(() => sanitizeTimeout(data.timeout));
  const [retries, setRetries] = useState<number>(() => sanitizeRetries(data.retries));
  const reactFlowInstance = useReactFlow();
  const translate = useTranslate();

  const removeEdgesByHandles = useCallback(
    (handles: string[]) => {
      if (handles.length === 0 || reactFlowInstance === undefined) {
        return;
      }
      const edges = reactFlowInstance.getEdges();
      const filteredEdges = edges.filter((edge) => {
        if (edge.sourceHandle === undefined || edge.sourceHandle === null) {
          return true;
        }
        return !handles.includes(edge.sourceHandle);
      });
      if (filteredEdges.length !== edges.length) {
        flowchartLogger.debug('IVRControls: deleting edges for handles', { handles });
        reactFlowInstance.setEdges(filteredEdges);
      }
    },
    [reactFlowInstance]
  );

  const updateIfValid = useCallback((): void => {
    if (
      // Have to check for null to avoid TS errors
      errorSoundfile === null ||
      menuSoundfile === null ||
      timeout < 1000 ||
      retries < 0 ||
      timeoutSoundfile === null
    ) {
      flowchartLogger.debug('IVRControls: invalid form data, update cancelled');
      return;
    }
    flowchartLogger.debug('IVRControls: node data updated');
    updateNodeData({ selections, errorSoundfile, menuSoundfile, timeout, retries, timeoutSoundfile });
  }, [errorSoundfile, menuSoundfile, timeout, retries, timeoutSoundfile, selections, updateNodeData]);

  useEffect(() => {
    const nextRetries = sanitizeRetries(data.retries);
    startTransition(() => {
      setRetries((current) => (current === nextRetries ? current : nextRetries));
    });
  }, [data.retries, sanitizeRetries]);

  useEffect(() => {
    const nextTimeout = sanitizeTimeout(data.timeout);
    startTransition(() => {
      setTimeoutProp((current) => (current === nextTimeout ? current : nextTimeout));
    });
  }, [data.timeout, sanitizeTimeout]);

  useEffect(() => {
    flowchartLogger.debug('IVRControls: updating selections', { selections });
    updateIfValid();
  }, [selections, errorSoundfile, menuSoundfile, timeoutSoundfile, updateIfValid]);

  const isFormInvalid = useCallback((): boolean => {
    const isValid =
      errorSoundfile === null ||
      menuSoundfile === null ||
      timeout < 1000 ||
      retries < 0 ||
      timeoutSoundfile === null ||
      selections.length === 0 ||
      selections.some((selection) => selection.value === '');
    flowchartLogger.debug('IVRControls: isFormInvalid', { isValid });
    return isValid;
  }, [errorSoundfile, menuSoundfile, timeout, retries, timeoutSoundfile, selections]);

  const handleSelectionDelete = useCallback(
    (handleId: string) => {
      flowchartLogger.debug('IVRControls: removing selection handle', { handleId });
      removeEdgesByHandles([handleId]);
      updateIfValid();
    },
    [removeEdgesByHandles, updateIfValid]
  );

  const onDataUpdate = useCallback((): void => {
    flowchartLogger.debug('IVRControls: onDataUpdate', {
      selections,
      errorSoundfile,
      menuSoundfile,
      timeout,
      retries,
      timeoutSoundfile,
    });
    updateIfValid();
  }, [errorSoundfile, menuSoundfile, timeout, retries, timeoutSoundfile, selections, updateIfValid]);

  return (
    <NodeControlForm
      onSubmit={(event) => {
        event.preventDefault();
        onDataUpdate();
      }}
      isDisabled={isFormInvalid()}
      data={data}
      updateNodeData={updateNodeData}
    >
      <SoundfileList
        soundfile={errorSoundfile !== null ? errorSoundfile.id : ''}
        label={translate('callflows.nodes.ivr.errorSoundfile')}
        setSoundfile={setErrorSoundfile}
      />
      <SoundfileList
        soundfile={menuSoundfile !== null ? menuSoundfile.id : ''}
        label={translate('callflows.nodes.ivr.menuSoundfile')}
        setSoundfile={setMenuSoundfile}
      />
      <SoundfileList
        soundfile={timeoutSoundfile !== null ? timeoutSoundfile.id : ''}
        label={translate('callflows.nodes.ivr.timeoutSoundfile')}
        setSoundfile={setTimeoutSoundfile}
      />
      <Divider sx={{ my: 1 }} />
      <TextField
        value={timeout}
        label={translate('callflows.nodes.ivr.timeout')}
        onChange={(event) => {
          setTimeoutProp(sanitizeTimeout(parseInt(event.target.value)));
        }}
        type='number'
        error={timeout < 1000}
        slotProps={{ htmlInput: { min: 1000 } }}
      />
      <TextField
        value={retries}
        label={translate('callflows.nodes.ivr.retries')}
        onChange={(event) => {
          setRetries(sanitizeRetries(parseInt(event.target.value)));
        }}
        type='number'
        error={retries < 0}
        slotProps={{ htmlInput: { min: 0 } }}
      />
      <span>{translate('callflows.nodes.ivr.selections')}</span>
      <Box overflow='auto'>
        <SortableInputsList
          inputs={selections}
          inputLabel={translate('callflows.nodes.ivr.selection')}
          onChange={setSelections}
          onDelete={handleSelectionDelete}
        />
      </Box>
    </NodeControlForm>
  );
}
